import React from "react";
import { Autocomplete, FormControl, TextField } from "@mui/material";
import {
  Controller,
  UseFormSetValue,
  UseFormTrigger,
  useFormContext,
} from "react-hook-form";

interface AutocompleteSelectProps {
  fieldName: string;
  label: string;
  fullWidth?: boolean;
  control: any;
  error?: boolean;
  helperText?: any;
  defaultValue?: string | null;
  options: any[];
  variant?: "filled" | "outlined" | "standard";
  valueKey: string;
  labelKey: string;
  disabled?: boolean;
  setValue: UseFormSetValue<any>;
  value?: string | number;
  trigger: UseFormTrigger<any>;
  onBlur?: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  onChange?: (event: React.SyntheticEvent, newValue: any) => void;
  loading?: boolean;
  onInputChange?: any;
  filterOptions?: any;
  freeSolo?: boolean;
  other?: any;
  // optional: provide yup schema so component can show required asterisk on mount
  yupSchema?: any;
  required?: boolean;
}

const AutocompleteSelect: React.FC<AutocompleteSelectProps> = ({
  fieldName,
  control,
  label,
  error,
  helperText,
  fullWidth,
  defaultValue,
  options,
  variant,
  valueKey,
  labelKey,
  disabled,
  value,
  setValue,
  trigger,
  onBlur,
  onChange,
  loading,
  onInputChange,
  freeSolo = true,
  filterOptions,
  other,
  yupSchema,
  required = false,
}) => {
  const methods = useFormContext();

  const computeIsRequired = React.useCallback(() => {
    if (required) return true;
    try {
      if (yupSchema) {
        if (typeof yupSchema.describe === "function") {
          const desc = yupSchema.describe();
          const f = desc?.fields?.[fieldName];
          if (f?.tests) return f.tests.some((t: any) => t?.name === "required");
        }
        const fieldSchema =
          yupSchema.fields?.[fieldName] ?? yupSchema?.[fieldName];
        if (fieldSchema) {
          const tests = fieldSchema.tests ?? fieldSchema._tests ?? [];
          if (Array.isArray(tests))
            return tests.some(
              (t: any) => t?.name === "required" || t?.TEST === "required",
            );
        }
      }
    } catch {
      /* ignore */
    }
    try {
      const fieldState = (methods as any)?.getFieldState
        ? (methods as any).getFieldState(fieldName)
        : null;
      return !!fieldState?.invalid;
    } catch {
      return false;
    }
  }, [required, yupSchema, fieldName, methods]);

  const [isRequiredState, setIsRequiredState] = React.useState<boolean>(() =>
    computeIsRequired(),
  );
  React.useEffect(() => {
    setIsRequiredState(computeIsRequired());
  }, [computeIsRequired]);

  const labelWithAsterisk = !label
    ? ""
    : isRequiredState
      ? `${label} *`
      : label;

  return (
    <FormControl
      fullWidth={fullWidth || true}
      sx={{
        color: "secondary.main",
        background: "none",
        // padding: "0 5px",
      }}
    >
      <Controller
        name={fieldName}
        control={control}
        defaultValue={defaultValue}
        render={({ field }) => (
          <Autocomplete
            {...field}
            options={options || []}
            getOptionLabel={(option: any) => {
              if (typeof option === 'string') return option;
              return option?.[labelKey] ?? "";
            }}
            getOptionKey={(option: any) => {
              if (typeof option === 'string') return option;
              return option?.[valueKey] ?? option;
            }}
            value={
              options?.find(
                (o) => value === o[valueKey] || value === String(o[valueKey]),
              ) || null
            }
            onChange={(event, selectedOption: any) => {
              const newVal = typeof selectedOption === 'string' ? selectedOption : (selectedOption?.[valueKey] ?? null);
              setValue(fieldName, newVal);
              trigger(fieldName);
              onChange && onChange(event, selectedOption);
            }}
            onInputChange={(event: any, newValue: string, reason: string) =>
              onInputChange?.(event, newValue, reason)
            }
            loading={loading}
            freeSolo={freeSolo}
            filterOptions={filterOptions}
            {...other}
            renderInput={(params) => (
              <TextField
                {...params}
                label={labelWithAsterisk}
                error={error}
                helperText={helperText}
                variant="outlined"
                onBlur={(
                  event: React.ChangeEvent<
                    HTMLInputElement | HTMLTextAreaElement
                  >,
                ) => {
                  field.onBlur();
                  onBlur && onBlur(event);
                }}
                InputLabelProps={{
                  sx: {
                    color: "secondary.main",
                    background: "none",
                    padding: "0 5px",
                  },
                }}
                sx={{
                  "& .MuiInputBase-root": {
                    height: 38,
                    backgroundColor: "rgba(63, 141, 124, 0.00)",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderWidth: "2px",
                    borderColor: "#08163B",
                    borderRadius: "8px",
                    color: "secondary.main",
                  },
                }}
              />
            )}
          />
        )}
      />
    </FormControl>
  );
};

export default AutocompleteSelect;
